import { Controller } from '@hotwired/stimulus'

// Controls visibility of action configuration fields based on selected action type
export default class extends Controller {
  static targets = ['reminderConfig', 'notificationConfig', 'repeatType', 'repeatIntervalFields', 'stopConditionFields', 'repeatSettings', 'templateSelect', 'templatePreview', 'previewLink', 'deliveryMethodGroup']
  static values = { taskableType: String }

  connect() {
    this.updateVisibility()
    // Filter out client-facing recipients for non-ClientTask types before
    // select2 reads the options. Must run synchronously here since select2
    // initialization is deferred via setTimeout below.
    this.filterRecipientOptionsByTaskableType()
    // Initialize select2 for any visible config on connect
    this.initializeVisibleSelect2()
    // Listen for trigger type changes to show/hide repeat settings
    this.boundHandleTriggerTypeChanged = this.handleTriggerTypeChanged.bind(this)
    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('automation:triggerTypeChanged', this.boundHandleTriggerTypeChanged)
    }
  }

  disconnect() {
    const form = this.element.closest('form')
    if (form && this.boundHandleTriggerTypeChanged) {
      form.removeEventListener('automation:triggerTypeChanged', this.boundHandleTriggerTypeChanged)
    }
  }

  handleTriggerTypeChanged(event) {
    const { isDateBased } = event.detail
    this.updateRepeatSettingsVisibility(isDateBased)
  }

  // Account Owner + Customers (non-BK360 users) only make sense for
  // Tasks::ClientTask, since they're the only tasks visible to client users.
  // Strip them from the recipient dropdown for every other task type, except
  // Account Owner on Tasks::TaxTask, which emails the owner tax return updates.
  //
  // Conversely on Tasks::ClientTask we strip employee-only roles and groups
  // so the dropdown only surfaces recipients the client side can actually
  // receive — mirrors the manual reminder recipient filter.
  filterRecipientOptionsByTaskableType() {
    if (this.taskableTypeValue === 'Tasks::ClientTask') {
      ['client_advisor', 'ca_manager'].forEach((value) => {
        this.element.querySelectorAll(`option[value="${value}"]`).forEach((opt) => opt.remove())
      })
      ;['Team Members', 'Employees', 'Groups'].forEach((label) => {
        this.element.querySelectorAll(`optgroup[label="${label}"]`).forEach((group) => group.remove())
      })
      return
    }

    if (this.taskableTypeValue !== 'Tasks::TaxTask') {
      this.element.querySelectorAll('option[value="account_owner"]').forEach((opt) => opt.remove())
    }
    this.element.querySelectorAll('optgroup[label="Customers"]').forEach((group) => group.remove())
  }

  updateRepeatSettingsVisibility(isDateBased) {
    if (this.hasRepeatSettingsTarget) {
      this.repeatSettingsTarget.classList.toggle('d-none', !isDateBased)
      this.setInputsDisabled(this.repeatSettingsTarget, !isDateBased)
    }
    // Also hide stop condition fields if not date-based
    if (this.hasStopConditionFieldsTarget && !isDateBased) {
      this.stopConditionFieldsTarget.classList.add('d-none')
      this.setInputsDisabled(this.stopConditionFieldsTarget, true)
      // Clear stop_status
      const stopStatusSelect = this.stopConditionFieldsTarget.querySelector('select')
      if (stopStatusSelect) {
        stopStatusSelect.value = ''
      }
    }
  }

  initializeVisibleSelect2() {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      if (this.hasReminderConfigTarget && !this.reminderConfigTarget.classList.contains('d-none')) {
        this.initializeSelect2InTarget(this.reminderConfigTarget)
      }
      if (this.hasNotificationConfigTarget && !this.notificationConfigTarget.classList.contains('d-none')) {
        this.initializeSelect2InTarget(this.notificationConfigTarget)
      }
    }, 100)
  }

  typeChanged(event) {
    this.updateVisibility()
  }

  repeatTypeChanged(event) {
    const repeatType = event.target.value
    const isRecurring = repeatType === 'every'

    if (this.hasRepeatIntervalFieldsTarget) {
      this.repeatIntervalFieldsTarget.classList.toggle('d-none', !isRecurring)
    }

    if (this.hasStopConditionFieldsTarget) {
      this.stopConditionFieldsTarget.classList.toggle('d-none', !isRecurring)
      // Clear stop_status when hiding
      if (!isRecurring) {
        const stopStatusSelect = this.stopConditionFieldsTarget.querySelector('select')
        if (stopStatusSelect) {
          stopStatusSelect.value = ''
        }
      }
    }
  }

  deliveryMethodChanged() {
    if (!this.hasDeliveryMethodGroupTarget) return

    const checkboxes = this.deliveryMethodGroupTarget.querySelectorAll('input[type="checkbox"]')
    const anyChecked = Array.from(checkboxes).some(cb => cb.checked)

    // If any checkbox is checked, remove required from all; otherwise add required to first
    checkboxes.forEach((cb, idx) => {
      cb.required = !anyChecked && idx === 0
    })
  }

  templateChanged(event) {
    const templateKey = event.target.value

    // Update description preview
    if (this.hasTemplatePreviewTarget) {
      if (!templateKey) {
        this.templatePreviewTarget.classList.add('d-none')
        this.templatePreviewTarget.textContent = ''
      } else {
        const previews = this.getTemplatePreviews()
        const preview = previews[templateKey]
        if (preview) {
          this.templatePreviewTarget.textContent = preview
          this.templatePreviewTarget.classList.remove('d-none')
        } else {
          this.templatePreviewTarget.classList.add('d-none')
          this.templatePreviewTarget.textContent = ''
        }
      }
    }

    // Update preview link and inline preview if visible
    if (this.hasPreviewLinkTarget) {
      const templatesWithPreview = this.getTemplatesWithPreview()

      const configContainer = this.previewLinkTarget.closest('[data-automation-action-target="notificationConfig"]')
      const previewContainer = configContainer?.querySelector('.notification-preview-inline')
      const previewIsVisible = previewContainer && !previewContainer.classList.contains('d-none')

      if (!templateKey) {
        this.previewLinkTarget.classList.add('d-none')
        // Hide inline preview if no template selected
        if (previewContainer) {
          previewContainer.classList.add('d-none')
          this.previewLinkTarget.innerHTML = '<i class="far fa-eye"></i> Preview'
        }
      } else if (templatesWithPreview.includes(templateKey)) {
        const newUrl = `/automations/notification_previews/${templateKey}`
        this.previewLinkTarget.dataset.previewUrl = newUrl
        this.previewLinkTarget.classList.remove('d-none')

        // If preview is already visible, update the iframe src
        if (previewIsVisible) {
          const iframe = previewContainer.querySelector('iframe')
          if (iframe) {
            iframe.src = newUrl
          }
        }
      } else {
        this.previewLinkTarget.classList.add('d-none')
        // Hide inline preview if template doesn't support preview
        if (previewContainer) {
          previewContainer.classList.add('d-none')
          this.previewLinkTarget.innerHTML = '<i class="far fa-eye"></i> Preview'
        }
      }
    }
  }

  openPreview(event) {
    event.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.previewUrl
    if (!url) return

    // Find or create the inline preview container
    const configContainer = button.closest('[data-automation-action-target="notificationConfig"]')
    let previewContainer = configContainer.querySelector('.notification-preview-inline')

    if (previewContainer) {
      // Toggle off if already showing
      if (!previewContainer.classList.contains('d-none')) {
        previewContainer.classList.add('d-none')
        button.innerHTML = '<i class="far fa-eye"></i> Preview'
        return
      }
    } else {
      // Create the preview container
      previewContainer = document.createElement('div')
      previewContainer.className = 'notification-preview-inline mt-3 border rounded'
      previewContainer.innerHTML = `
        <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 border-bottom">
          <span class="small text-muted fw-medium">Email Preview</span>
          <button type="button" class="btn-close btn-sm" data-action="click->automation-action#closePreview" aria-label="Close"></button>
        </div>
        <div class="preview-content">
          <iframe style="width: 100%; height: 400px; border: none;"></iframe>
        </div>
      `
      configContainer.appendChild(previewContainer)
    }

    // Show and load the preview
    const iframe = previewContainer.querySelector('iframe')
    iframe.src = url
    previewContainer.classList.remove('d-none')
    button.innerHTML = '<i class="far fa-eye-slash"></i> Hide'
  }

  closePreview(event) {
    event.preventDefault()
    const previewContainer = event.currentTarget.closest('.notification-preview-inline')
    if (previewContainer) {
      previewContainer.classList.add('d-none')
      // Reset button text
      const configContainer = previewContainer.closest('[data-automation-action-target="notificationConfig"]')
      const previewButton = configContainer.querySelector('[data-automation-action-target="previewLink"]')
      if (previewButton) {
        previewButton.innerHTML = '<i class="far fa-eye"></i> Preview'
      }
    }
  }

  updateVisibility() {
    const select = this.element.querySelector('select[name*="action_type"]')
    if (!select) return

    const actionType = select.value

    // Hide all config sections first
    this.hideAll()

    // Show relevant config based on action type
    switch (actionType) {
      case 'send_reminder':
        this.showTarget('reminderConfig')
        break
      case 'send_notification':
        this.showTarget('notificationConfig')
        break
    }
  }

  hideAll() {
    if (this.hasReminderConfigTarget) {
      this.reminderConfigTarget.classList.add('d-none')
      this.setInputsDisabled(this.reminderConfigTarget, true)
    }
    if (this.hasNotificationConfigTarget) {
      this.notificationConfigTarget.classList.add('d-none')
      this.setInputsDisabled(this.notificationConfigTarget, true)
    }
  }

  showTarget(targetName) {
    const target = this[`${targetName}Target`]
    if (target) {
      target.classList.remove('d-none')
      this.setInputsDisabled(target, false)
      this.initializeSelect2InTarget(target)

      // Update delivery method required state if showing notification config
      if (targetName === 'notificationConfig') {
        this.deliveryMethodChanged()
      }
    }
  }

  // Disable/enable all inputs within a container to prevent hidden fields from being submitted
  setInputsDisabled(container, disabled) {
    if (!container) return
    container.querySelectorAll('input, select, textarea').forEach(input => {
      input.disabled = disabled
    })
  }

  getTemplatePreviews() {
    if (this.hasNotificationConfigTarget) {
      try {
        return JSON.parse(this.notificationConfigTarget.dataset.templatePreviews || '{}')
      } catch (e) {
        console.error('Failed to parse template previews', e)
      }
    }
    return {}
  }

  getTemplatesWithPreview() {
    if (this.hasNotificationConfigTarget) {
      try {
        return JSON.parse(this.notificationConfigTarget.dataset.templatesWithPreview || '[]')
      } catch (e) {
        console.error('Failed to parse templates with preview', e)
      }
    }
    return []
  }

  initializeSelect2InTarget(target) {
    // Small delay to ensure the element is fully visible before initializing select2
    setTimeout(() => {
      target.querySelectorAll('[data-controller~="select2"]').forEach(select => {
        const select2Controller = this.application.getControllerForElementAndIdentifier(select, 'select2')
        if (select2Controller) {
          // Destroy existing select2 instance and reinitialize
          try {
            if (typeof select2Controller.disconnect === 'function') {
              select2Controller.disconnect()
            }
          } catch (e) {
            // Ignore errors from destroying non-existent instance
          }
          if (typeof select2Controller.connect === 'function') {
            select2Controller.connect()
          }
        } else {
          // Controller not yet connected, trigger Stimulus to connect it
          // This happens when element was hidden on page load
          if (window.$ && window.$.fn.select2) {
            // Initialize select2 directly as fallback
            const $select = $(select)
            const options = { minimumResultsForSearch: 1 }

            // Parse data attributes for select2 options
            const data = $select.data()
            for (const item in data) {
              if (item.indexOf('select2') > -1) {
                const optionName = item.replace('select2', '').replace(/^./, c => c.toLowerCase())
                if (optionName) {
                  options[optionName] = data[item]
                }
              }
            }

            $select.select2(options)
          }
        }
      })
    }, 50)
  }
}
